function MegaMenuFullscreenFactory(container) {
  class MegaMenuFullscreen {
    constructor(container) {
      this.megaMenuFullscreen = container;
      this.filterTogglers = document.querySelectorAll(
        `[data-toggle-mega-menu="${this.megaMenuFullscreen.getAttribute(
          "data-mega-menu"
        )}"]`
      );
      this._outsideClickListener = null;
      this._hoverOutTimer = null;
      this._isHoveringMenu = false;
      this._isHoveringToggler = false;
      this._activeToggler = null;
      this.tl;
      this._init();
    }

    _init() {
      this._setEvents();
    }

    _setEvents() {
      for (const filterToggler of this.filterTogglers) {
        const triggerMode = filterToggler.getAttribute("trigger");
        const isHover = triggerMode === "hover" && window.matchMedia("(pointer: fine)").matches;

        // Prevent native <details> toggle in ALL modes so the JS controls
        // the open/close state entirely (avoids conflicts with GSAP animations)
        const summary = filterToggler.querySelector("summary");
        if (summary) {
          summary.addEventListener("click", (event) => {
            event.preventDefault();
            if (isHover) {
              const url = summary.getAttribute("data-url");
              if (url) {
                window.location.href = url;
              }
            }
          });
        }

        filterToggler.addEventListener("click", (event) => {
          event.stopPropagation();
          if (isHover) {
            // On hover mode, click navigates to the URL if available
            const url = filterToggler.querySelector("summary")?.getAttribute("data-url")
              || filterToggler.querySelector("[data-url]")?.getAttribute("data-url");
            if (url) {
              window.location.href = url;
            }
            return;
          }
          this._toggleMenu(filterToggler);
        });

        if (isHover) {
          filterToggler.addEventListener("mouseenter", () => {
            this._isHoveringToggler = true;
            clearTimeout(this._hoverOutTimer);

            // Kill any ongoing fade-out animation so the menu can reopen
            if (this.tl) {
              this.tl.kill();
              this.tl = null;
            }

            if (!this.megaMenuFullscreen.classList.contains("--active")) {
              this._closeOtherMenus(filterToggler);
              this._fadeInMenu(filterToggler);
            } else {
              // Menu is already active (possibly mid-fade-out) — restore full opacity
              gsap.set(this.megaMenuFullscreen, { opacity: 1 });
            }
          });

          filterToggler.addEventListener("mouseleave", () => {
            this._isHoveringToggler = false;
            this._hoverOutTimer = setTimeout(() => {
              if (!this._isHoveringMenu) {
                this._fadeOutMenu();
              }
            }, 500);
          });

          this.megaMenuFullscreen.addEventListener("mouseenter", () => {
            clearTimeout(this._hoverOutTimer);
            this._isHoveringMenu = true;
          });

          this.megaMenuFullscreen.addEventListener("mouseleave", () => {
            this._isHoveringMenu = false;
            this._hoverOutTimer = setTimeout(() => {
              if (!this._isHoveringToggler) {
                this._fadeOutMenu();
              }
            }, 500);
          });
        }
      }
    }

    _closeOtherMenus(filterToggler) {
      let megamenuTarget = filterToggler.getAttribute("data-toggle-mega-menu");
      for (const mega_menu of document.querySelectorAll("[data-mega-menu].--active")) {
        let megamenuReference = mega_menu.getAttribute("data-mega-menu");
        if (megamenuTarget !== megamenuReference) {
          mega_menu.classList.remove("--active");
          gsap.set(mega_menu, { opacity: 0 });
          const toggler = document.querySelector(`[data-toggle-mega-menu="${megamenuReference}"]`);
          if (toggler) toggler.removeAttribute("open");
        }
      }
    }

    _toggleMenu(filterToggler) {
      this._closeOtherMenus(filterToggler);
      if (this.megaMenuFullscreen.classList.contains("--active")) {
        this._fadeOutMenu();
      } else {
        this._fadeInMenu(filterToggler);
      }
    }

    _fadeInMenu(toggler) {
      // Kill any previous timeline to avoid stacking animations
      if (this.tl) {
        this.tl.kill();
      }
      this.tl = gsap.timeline();
      this.megaMenuFullscreen.classList.add("--active");

      // Track the active toggler and set its open attribute for CSS styles
      this._activeToggler = toggler || null;
      if (this._activeToggler) {
        this._activeToggler.setAttribute("open", "");
      }

      // Animate the fade-in with GSAP
      this.tl.fromTo(
        this.megaMenuFullscreen,
        {
          opacity: 0,
        },
        {
          opacity: 1,
          duration: 0.15,
        }
      );

      this.tl.fromTo(
        this.megaMenuFullscreen.querySelectorAll(
          ".mega-menu__nav > li, .navigation-promo__wrapper"
        ),
        {
          opacity: 0,
          y: -10,
        },
        {
          opacity: 1,
          y: 0,
          duration: 0.15,
          stagger: 0.1,
        }
      );
      // Enable click outside detection
      this._enableClickOutside();
    }

    _fadeOutMenu() {
      // Kill any previous timeline to avoid stacking animations
      if (this.tl) {
        this.tl.kill();
      }
      this.tl = gsap.timeline({
        onComplete: () => {
          this.megaMenuFullscreen.classList.remove("--active");
          // Remove the open attribute from the toggler
          if (this._activeToggler) {
            this._activeToggler.removeAttribute("open");
            this._activeToggler = null;
          }
          this._disableClickOutside();
        },
      });
      this.tl.to(this.megaMenuFullscreen, {
        opacity: 0,
        duration: 0.45,
        ease: "power2.inOut",
      });
    }

    _enableClickOutside() {
      // Remove any previous listener before adding a new one
      this._disableClickOutside();
      this._outsideClickListener = this._onClickOutside(
        this.megaMenuFullscreen,
        () => {
          this._fadeOutMenu(); // Close the menu when clicking outside
        }
      );
    }

    _disableClickOutside() {
      // Remove the outside click listener when the menu is closed
      if (this._outsideClickListener) {
        this._outsideClickListener(); // Call the function returned by _onClickOutside to remove listener
        this._outsideClickListener = null;
      }
    }

    _onClickOutside(element, callback) {
      const filterTogglers = this.filterTogglers;
      // Detect clicks outside of the specified element
      function handleClickOutside(event) {
        // Ignore clicks on the togglers themselves (they have their own handler)
        for (const toggler of filterTogglers) {
          if (toggler.contains(event.target)) return;
        }
        if (!element.contains(event.target)) {
          callback(); // Trigger callback if clicked outside
        }
      }

      // Attach the event listener to the document
      document.addEventListener("click", handleClickOutside);

      // Return a function to remove the event listener
      return function removeClickListener() {
        document.removeEventListener("click", handleClickOutside);
      };
    }
  }

  return new MegaMenuFullscreen(container);
}
